import React, { useEffect, useMemo, useState } from "react";
import PageContainer from "../../layouts/PageContainer";
import StarterPanel from "../../components/Dashboard/StarterPanel";
import PageHeader from "../../components/Nav/PageHeader";
import WifiMetricMiniCard from "../../components/Dashboard/WifiMetricMiniCard";
import { numberFormat, tell_when } from "../../util/araknerdlib";
import { useSales } from "../../DataStore/SalesContext";
import { Skeleton, SkeletonBar, SkeletonPill } from "../../util/skeleton/Skeleton";
import AgentFloatModal from "../../components/Vendor/AgentFloatModal";
import { useAuth } from "../../DataStore/AuthContext";

const isOnlineByHeartbeat = (heartbeatAt, nowMs, maxAgeMs = 2 * 60 * 1000) => {
  if (!heartbeatAt) return false;

  const heartbeatTime = new Date(heartbeatAt).getTime();
  if (Number.isNaN(heartbeatTime)) return false;

  return nowMs - heartbeatTime <= maxAgeMs;
};

const timeAgo = (dateStr, nowMs) => {
  if (!dateStr) return "Never";

  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return "—";

  const diff = Math.max(0, nowMs - t);

  const sec = Math.floor(diff / 1000);
  if (sec < 10) return "Just now";
  if (sec < 60) return `${sec}s ago`;

  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;

  const day = Math.floor(hr / 24);
  return `${day}d ago`;
};


/* ---------------- Metrics Row ---------------- */

const MetricsRow = ({ totalAgents, floatBalance, revenueToday }) => (
  <div className="d-flex justify-content-end align-items-center py-3 px-4 rounded">
    <WifiMetricMiniCard
      label="Total Agents"
      value={numberFormat(totalAgents)}
      iconColor="text-warning"
      icon="fas fa-users"
    />
    <div className="border-left mx-3" style={{ height: 30 }} />
    <WifiMetricMiniCard
      label="Float Balance"
      value={numberFormat(floatBalance)}
      iconColor="text-success"
      icon="fas fa-wallet"
    />
    <div className="border-left mx-3" style={{ height: 30 }} />
    <WifiMetricMiniCard
      label="Daily Revenue"
      value={numberFormat(revenueToday)}
      iconColor="text-danger"
      icon="fas fa-coins"
    />
  </div>
);

/* ---------------- Page ---------------- */

const SalesAgentPage = () => {
  const { getVendors, vendors } = useSales();

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [modalType, setModalType] = useState(null);
  const {connectedProfiles} = useAuth();
  const currency = connectedProfiles?.currency || ' ';


  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // update every 30 seconds so status flips quickly (you can set to 60s too)
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);

    return () => clearInterval(t);
    }, []);


  useEffect(() => {
    getVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Normalize vendors ---------- */
  const agents = useMemo(() => {
  if (!Array.isArray(vendors)) return [];

  return vendors.map((v) => {
    const online = isOnlineByHeartbeat(v.heartbeat_at, now);

    return {
      id: v.user_id,
      name: `${v.first_name} ${v.last_name}`.trim(),
      phone: v.phone || "—",
      email: v.email,
      status: online ? "online" : "offline",

      // you can keep this raw timestamp if you want to show it later
      heartbeatAt: v.heartbeat_at,

      lastSeen: v.heartbeat_at
        ? new Date(v.heartbeat_at).toLocaleTimeString()
        : "—",
      lastSeenAgo: timeAgo(v.heartbeat_at, now),

      floatUGX: v.stats?.float_balance || 0,
      salesTodayUGX: v.stats?.total_revenue_today || 0,
      vouchersToday: v.stats?.total_daily_vouchers_sold || 0,

      lastTopup: v.last_activity?.last_deposit
        ? `${currency} ${numberFormat(v.last_activity.last_deposit.amount)} • ${ tell_when(v.last_activity.last_deposit.created_at)}`
        : "—",

      lastDeduct: v.last_activity?.last_withdraw
        ? `${currency} ${numberFormat(v.last_activity.last_withdraw.amount)} • ${ tell_when(v.last_activity.last_withdraw.created_at)}`
        : "—",
    };
  });
}, [vendors, now]);



  /* ---------- Filters ---------- */
  const filteredAgents = useMemo(() => {
    const q = query.toLowerCase().trim();

    return agents.filter((a) => {
      const matchesQuery =
        !q ||
        a.name.toLowerCase().includes(q) ||
        a.phone.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "all" ? true : a.status === statusFilter;

      return matchesQuery && matchesStatus;
    });
  }, [agents, query, statusFilter]);

  /* ---------- KPIs ---------- */
  const kpis = useMemo(() => {
    const totalAgents = agents.length;
    const floatOutstanding = agents.reduce((s, a) => s + a.floatUGX, 0);
    const todaySales = agents.reduce((s, a) => s + a.salesTodayUGX, 0);
    return { totalAgents, floatOutstanding, todaySales };
  }, [agents]);

  const money = (n) => `${currency} ${numberFormat(n)}`;

  /* ---------- Modal helpers ---------- */
  const openModal = (agent, type) => {
    setSelectedAgent(agent);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedAgent(null);
    setModalType(null);
  };

  /* ================= Render ================= */

  return (<>
    <PageContainer>
      <PageHeader
        title="Sales Agents"
        breadcrumb={
          <>
            <li className="breadcrumb-item">Payments</li>
            <li className="breadcrumb-item active">Sales Agents</li>
          </>
        }
        primaryButtons={[
          
            {
                label: 'Refresh',
                icon: 'fas fa-sync',
                className: 'btn-outline-secondary',
                onClick: () => getVendors(true)
            }
        ]}
      />

        <div className="row">
            <StarterPanel />
            <div className="col-lg-8 mb-4">
               {vendors === null ? (
                    <SkeletonBar h={48} />
                ) : (
                    <MetricsRow
                    totalAgents={kpis.totalAgents}
                    floatBalance={kpis.floatOutstanding}
                    revenueToday={kpis.todaySales}
                    />
                )}
            </div>  
        </div>


      {/* Info banner */}
      <div className="alert alert-info d-flex align-items-center mb-3">
        <i className="fe fe-credit-card mr-2 tx-18" />
        <div>
          <strong>Heads up:</strong> Sales Agents are users assigned the{" "}
          <b>Billing</b> role in <b>System Settings</b>.
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body">
          {/* Toolbar */}
          <div className="d-flex flex-wrap justify-content-between mb-3">
            <div className="d-flex flex-wrap">
              <input
                className="form-control mr-2 mb-2"
                style={{ width: 260 }}
                placeholder="Search agent…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <select
                className="form-control mr-2 mb-2"
                style={{ width: 180 }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Status: All</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </div>
          </div>

          {/* States */}
          {vendors === null && (
            <>
              {[...Array(5)].map((_, i) => (
                <div key={i} className="mb-3">
                  <Skeleton w="60%" />
                  <SkeletonPill w={120} />
                </div>
              ))}
            </>
          )}

          {vendors === "404" && (
            <div className="text-center py-5 text-muted">
              <div>No sales agents found.</div>
            </div>
          )}

          {Array.isArray(vendors) && (
            <div className="table-responsive">
              <table className="table table-hover table-borderless">
                <thead className="thead-light">
                  <tr>
                    <th>Agent</th>
                    <th>Status</th>
                    <th className="text-right">Float</th>
                    <th className="text-right">Sales Today</th>
                    <th>Last Activity</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgents.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <strong>{a.name}</strong>
                        <div className="small text-muted">
                          {a.phone} • {a.email}
                        </div>
                      </td>
                     <td>
                        <span
                            className={`badge badge-${
                            a.status === "online" ? "success" : "secondary"
                            }`}
                        >
                            {a.status}
                        </span>

                        <div className="small text-muted">
                            Last seen: {a.lastSeenAgo}
                        </div>
                        </td>

                      <td className="text-right font-weight-bold">
                        {money(a.floatUGX)}
                      </td>
                      <td className="text-right">
                        {money(a.salesTodayUGX)}
                        <div className="small text-muted">
                          {a.vouchersToday} vouchers
                        </div>
                      </td>
                      <td className="small">
                        <div>Topup: {a.lastTopup}</div>
                        <div>Deduct: {a.lastDeduct}</div>
                      </td>
                      <td className="text-right">
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => openModal(a, "deposit")}
                          >
                            Deposit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openModal(a, "withdraw")}
                          >
                            Withdraw
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

    </PageContainer>
    <AgentFloatModal
        show={modalType !== null}
        onClose={closeModal}
        onSuccess={() => {
          closeModal();
          getVendors(true);
        }}
        selectedAgent={selectedAgent}
        modalType={modalType}
      />
    </>
  );
};

export default SalesAgentPage;
